import { useState, useRef, useEffect, useDeferredValue, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDataStore } from '../../store/dataStore';
import type { ModelOffering } from '@bedrock-profiles/shared';
import { normalizeModelId, keyToDisplayName } from '../../utils/modelFamilies';
import { generateModelSlug } from '../../utils/unifiedModels';
import { getActiveCatalogOfferings } from '../../services/catalog';
import { trackEvent } from '../../services/analytics';

interface SearchResult {
  type: 'offering' | 'family' | 'provider';
  label: string;
  sublabel: string;
  url: string;
}

export function GlobalSearch() {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const deferredQuery = useDeferredValue(query);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const currentSnapshot = useDataStore((state) => state.currentSnapshot);

  const closeSearch = useCallback(() => {
    setIsOpen(false);
    triggerRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    inputRef.current?.focus();
  }, [isOpen]);

  // Cmd+K to open
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
      }
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        closeSearch();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [closeSearch, isOpen]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        closeSearch();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [closeSearch, isOpen]);

  const results = useMemo((): SearchResult[] => {
    if (!deferredQuery.trim()) return [];

    const q = deferredQuery.toLowerCase();
    const offerings = getActiveCatalogOfferings(currentSnapshot);

    // Collect unique matches
    const seen = new Set<string>();
    const results: SearchResult[] = [];

    // Match model families
    const familyMatches = new Map<string, ModelOffering>();
    for (const offering of offerings) {
      const modelName = offering.model.modelName?.toLowerCase() ?? '';
      const modelId = offering.model.modelId.toLowerCase();
      const provider = offering.model.provider.toLowerCase();

      if (modelName.includes(q) || modelId.includes(q) || provider.includes(q)) {
        const normalizedId = normalizeModelId(offering.model.modelId);
        const familyKey = `${offering.model.provider}::${normalizedId}`;
        if (!familyMatches.has(familyKey)) {
          familyMatches.set(familyKey, offering);
        }
      }
    }

    // Add family matches first (up to 5)
    let count = 0;
    for (const [mapKey, offering] of familyMatches) {
      if (count >= 5) break;
      const key = `family-${mapKey}`;
      if (!seen.has(key)) {
        seen.add(key);
        const [, normalizedId] = mapKey.split('::');
        results.push({
          type: 'family',
          label: keyToDisplayName(normalizedId),
          sublabel: `${offering.model.provider} · Model family`,
          url: `/models/${encodeURIComponent(generateModelSlug(normalizedId))}`,
        });
        count++;
      }
    }

    // Add specific offering matches (up to 5)
    count = 0;
    for (const offering of offerings) {
      if (count >= 5) break;
      const offeringId = offering.offeringId.toLowerCase();
      const modelId = offering.model.modelId.toLowerCase();
      const displayName = (offering.displayName || '').toLowerCase();

      if (offeringId.includes(q) || modelId.includes(q) || displayName.includes(q)) {
        const key = `offering-${offering.offeringId}`;
        if (!seen.has(key)) {
          seen.add(key);
          results.push({
            type: 'offering',
            label: offering.displayName || offering.offeringId,
            sublabel: `${offering.model.provider} · ${offering.deployment.cloudProvider}`,
            url: `/profile/${encodeURIComponent(offering.offeringId)}`,
          });
          count++;
        }
      }
    }

    return results;
  }, [deferredQuery, currentSnapshot]);

  const handleSelect = useCallback((result: SearchResult) => {
    trackEvent('search_result_clicked', {
      query_length_bucket: query.length < 4 ? 'short' : query.length < 16 ? 'medium' : 'long',
      query_word_count: query.trim().split(/\s+/).filter(Boolean).length,
      result_type: result.type,
      result_url: result.url,
      source: 'global_search',
    });
    navigate(result.url);
    closeSearch();
    setQuery('');
  }, [closeSearch, navigate, query]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (results.length > 0 && results[selectedIndex]) {
        handleSelect(results[selectedIndex]);
      } else if (query.trim()) {
        trackEvent('search_submitted', {
          query_length_bucket: query.trim().length < 4 ? 'short' : query.trim().length < 16 ? 'medium' : 'long',
          query_word_count: query.trim().split(/\s+/).filter(Boolean).length,
          source: 'global_search',
        });
        navigate(`/search?q=${encodeURIComponent(query.trim())}`);
        closeSearch();
        setQuery('');
      }
    } else if (e.key === 'Tab' && dialogRef.current) {
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled])')
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        data-testid="global-search-trigger"
        aria-label="Open global search"
        onClick={() => setIsOpen(true)}
        className="inline-flex h-11 min-w-11 items-center justify-center gap-2 rounded-md px-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 hover:text-slate-950 sm:h-9 sm:min-w-0"
      >
        <svg
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.7} d="M21 21l-5.2-5.2m1.7-4.3a6 6 0 1 1-12 0 6 6 0 0 1 12 0Z" />
        </svg>
        <span className="hidden xl:inline">Search</span>
        <kbd className="hidden rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-slate-500 sm:inline-flex">
          {navigator.platform?.includes('Mac') ? '⌘' : 'Ctrl'}K
        </kbd>
      </button>

      {isOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Global search"
          className="fixed inset-0 z-50 bg-slate-950/20 px-4 pt-16 backdrop-blur-[1px]"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              event.preventDefault();
              closeSearch();
            }
          }}
        >
          <div ref={dialogRef} className="relative mx-auto w-full max-w-2xl">
            <div className="relative">
              <svg
                className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.7} d="M21 21l-5.2-5.2m1.7-4.3a6 6 0 1 1-12 0 6 6 0 0 1 12 0Z" />
              </svg>
              <input
                ref={inputRef}
                type="text"
                data-testid="global-search-input"
                aria-label="Search models, publishers, inference providers, and offerings"
                aria-expanded={results.length > 0}
                aria-controls="global-search-results"
                aria-activedescendant={results[selectedIndex] ? `global-search-option-${selectedIndex}` : undefined}
                aria-autocomplete="list"
                role="combobox"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setSelectedIndex(0);
                }}
                onKeyDown={handleKeyDown}
                placeholder="Search models, publishers, regions, capabilities..."
                className="h-12 w-full rounded-lg border border-slate-200 bg-white pl-10 pr-14 text-sm text-slate-950 shadow-2xl shadow-slate-900/15 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
              <button
                type="button"
                aria-label="Close search"
                onClick={() => {
                  closeSearch();
                }}
                className="absolute right-1 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-md text-slate-500 transition hover:bg-slate-100 hover:text-slate-950 sm:right-2 sm:h-8 sm:w-8"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M6 18 18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {results.length > 0 && (
              <div
                id="global-search-results"
                role="listbox"
                data-testid="global-search-results"
                className="mt-2 max-h-80 overflow-y-auto overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl shadow-slate-900/10"
              >
                {results.map((result, i) => (
                  <button
                    id={`global-search-option-${i}`}
                    key={`${result.type}-${result.url}`}
                    role="option"
                    aria-selected={i === selectedIndex}
                    aria-label={`Open ${result.label}: ${result.sublabel}`}
                    data-result-type={result.type}
                    data-result-url={result.url}
                    onClick={() => handleSelect(result)}
                    onMouseEnter={() => setSelectedIndex(i)}
                    className={`w-full text-left px-3 py-2 flex items-center gap-3 text-sm transition-colors ${
                      i === selectedIndex ? 'bg-blue-50' : 'hover:bg-slate-50'
                    }`}
                  >
                    <span className={`text-[10px] uppercase font-medium px-1.5 py-0.5 rounded ${
                      result.type === 'family'
                        ? 'bg-slate-100 text-slate-700'
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {result.type === 'family' ? 'Model' : 'Offering'}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="break-words font-medium text-slate-950 [overflow-wrap:anywhere]">
                        {result.label}
                      </div>
                      <div className="break-words text-xs text-slate-500 [overflow-wrap:anywhere]">
                        {result.sublabel}
                      </div>
                    </div>
                  </button>
                ))}
                {query.trim() && (
                  <button
                    onClick={() => {
                      trackEvent('search_submitted', {
                        query_length_bucket: query.trim().length < 4 ? 'short' : query.trim().length < 16 ? 'medium' : 'long',
                        query_word_count: query.trim().split(/\s+/).filter(Boolean).length,
                        source: 'global_search_dropdown',
                      });
                      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
                      closeSearch();
                      setQuery('');
                    }}
                    className="w-full border-t border-slate-100 px-3 py-2 text-left text-sm font-medium text-blue-700 hover:bg-blue-50"
                  >
                    Search all for &ldquo;{query.trim()}&rdquo; &rarr;
                  </button>
                )}
              </div>
            )}

            {query.trim() && results.length === 0 && (
              <button
                type="button"
                onClick={() => {
                  trackEvent('search_submitted', {
                    query_length_bucket: query.trim().length < 4 ? 'short' : query.trim().length < 16 ? 'medium' : 'long',
                    query_word_count: query.trim().split(/\s+/).filter(Boolean).length,
                    source: 'global_search_dropdown',
                  });
                  navigate(`/search?q=${encodeURIComponent(query.trim())}`);
                  closeSearch();
                  setQuery('');
                }}
                className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-3 text-left text-sm font-medium text-blue-700 shadow-xl shadow-slate-900/10 hover:bg-blue-50"
              >
                Search all for &ldquo;{query.trim()}&rdquo; &rarr;
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
